import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Mail, Phone, FileUp, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "./api";

const STATUSES = ["New", "In Review", "Accepted", "Declined", "Completed"];
const statusColor = {
  New: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "In Review": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Accepted: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Declined: "bg-red-500/15 text-red-300 border-red-500/30",
  Completed: "bg-neutral-500/15 text-neutral-300 border-neutral-500/30",
};

const Metric = ({ label, value }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
    <p className="font-heading text-3xl font-semibold">{value}</p>
    <p className="mt-1 text-sm text-neutral-400">{label}</p>
  </div>
);

export default function Inquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [notes, setNotes] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inquiries");
      setItems(data);
    } catch {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const metrics = useMemo(
    () => ({
      total: items.length,
      unread: items.filter((i) => (i.status || "New") === "New").length,
      active: items.filter((i) => ["In Review", "Accepted"].includes(i.status)).length,
    }),
    [items]
  );

  const open = (it) => {
    setActive(it);
    setNotes(it.internal_notes || "");
    setEmailBody("");
  };

  const patch = async (changes) => {
    const { data } = await api.patch(`/inquiries/${active.id}`, changes);
    setActive(data);
    setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
  };

  const changeStatus = async (status) => {
    try {
      await patch({ status });
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const saveNotes = async () => {
    try {
      await patch({ internal_notes: notes });
      toast.success("Notes saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const sendReply = async () => {
    if (!emailBody.trim()) return toast.error("Write a message first");
    setSending(true);
    try {
      await api.post(`/inquiries/${active.id}/email`, {
        subject: "A message from A Flat Records",
        message: emailBody,
      });
      toast.success("Reply sent to client");
      setEmailBody("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const toQuote = () => {
    navigate("/admin/invoices", { state: { fromInquiry: active } });
  };

  return (
    <div>
      <h1 className="mb-6 font-heading text-2xl font-semibold">Inquiries Feed</h1>
      <div className="mb-8 grid grid-cols-3 gap-4">
        <Metric label="Total inquiries" value={metrics.total} />
        <Metric label="New / unread" value={metrics.unread} />
        <Metric label="Active briefs" value={metrics.active} />
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/10">
        <table className="w-full text-left text-sm" data-testid="inquiries-table">
          <thead className="bg-white/[0.03] text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-3">Client</th>
              <th className="hidden px-4 py-3 sm:table-cell">Service</th>
              <th className="hidden px-4 py-3 md:table-cell">Release</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-neutral-500">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-neutral-500">
                  No inquiries yet.
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr
                  key={it.id}
                  onClick={() => open(it)}
                  data-testid={`inquiry-row-${it.id}`}
                  className="cursor-pointer border-t border-white/5 transition hover:bg-white/[0.04]"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium">{it.full_name}</div>
                    <div className="text-xs text-neutral-500">{it.email}</div>
                  </td>
                  <td className="hidden px-4 py-3 text-neutral-300 sm:table-cell">{it.service}</td>
                  <td className="hidden px-4 py-3 text-neutral-300 md:table-cell">{it.budget}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full border px-2.5 py-1 text-xs ${
                        statusColor[it.status || "New"]
                      }`}
                    >
                      {it.status || "New"}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {active && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="inquiry-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setActive(null)} />
          <div className="relative h-full w-full max-w-md overflow-y-auto border-l border-white/10 bg-[#0e0e11] p-6">
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="font-heading text-xl font-semibold">{active.full_name}</h2>
                <p className="text-sm text-neutral-500">
                  {new Date(active.created_at).toLocaleString("en-ZA", {
                    timeZone: "Africa/Johannesburg",
                  })}
                </p>
              </div>
              <button onClick={() => setActive(null)} data-testid="drawer-close" className="text-neutral-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="mb-4 flex gap-2">
              <a
                href={`mailto:${active.email}`}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/10 py-2.5 text-sm hover:bg-white/10"
              >
                <Mail size={15} /> Email
              </a>
              {active.phone ? (
                <a
                  href={`tel:${active.phone}`}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/10 py-2.5 text-sm hover:bg-white/10"
                >
                  <Phone size={15} /> Call
                </a>
              ) : null}
            </div>

            <dl className="mb-5 space-y-2 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm">
              {[
                ["Email", active.email],
                ["Phone", active.phone || "—"],
                ["Service", active.service],
                ["Release type", active.budget],
                ["Timeline", active.timeline],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="text-neutral-500">{k}</dt>
                  <dd className="text-right text-neutral-200">{v}</dd>
                </div>
              ))}
            </dl>

            <p className="mb-1 text-xs uppercase tracking-widest text-neutral-500">Project brief</p>
            <p className="mb-5 whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-neutral-200">
              {active.details}
            </p>

            <p className="mb-2 text-xs uppercase tracking-widest text-neutral-500">Status</p>
            <div className="mb-5 flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  data-testid={`status-${s.replace(/\s/g, "-")}`}
                  onClick={() => changeStatus(s)}
                  className={`rounded-full border px-3 py-1.5 text-xs transition ${
                    (active.status || "New") === s
                      ? statusColor[s]
                      : "border-white/10 text-neutral-400 hover:bg-white/10"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <p className="mb-2 text-xs uppercase tracking-widest text-neutral-500">Internal notes</p>
            <textarea
              data-testid="internal-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-[90px] w-full rounded-xl border border-white/10 bg-white/[0.04] p-3 text-sm outline-none focus:border-white/40"
              placeholder="Private studio notes…"
            />
            <button
              onClick={saveNotes}
              data-testid="save-notes"
              className="mt-2 w-full rounded-xl border border-white/10 py-2.5 text-sm hover:bg-white/10"
            >
              Save notes
            </button>

            <p className="mb-2 mt-6 text-xs uppercase tracking-widest text-neutral-500">
              Reply to client
            </p>
            <textarea
              data-testid="reply-body"
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              className="min-h-[90px] w-full rounded-xl border border-white/10 bg-white/[0.04] p-3 text-sm outline-none focus:border-white/40"
              placeholder="Type a booking confirmation, update, or decline…"
            />
            <button
              onClick={sendReply}
              data-testid="send-reply"
              disabled={sending}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-white py-2.5 text-sm font-medium text-black hover:bg-neutral-200 disabled:opacity-60"
            >
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send email
            </button>

            <button
              onClick={toQuote}
              data-testid="generate-quote"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-white/20 py-3 text-sm font-medium hover:bg-white/10"
            >
              <FileUp size={15} /> Generate Quote from Inquiry
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
